import React, { useState, useEffect, useCallback } from 'react'
import { cn } from '@/lib/utils/cn'
import { Button } from '@/design-system/Button'
import { Switch } from '@/design-system/Switch'
import { useTranslation } from 'react-i18next'
import { useUIStore } from '@/store/uiStore'

/* ------------------------------------------------------------------ */
/*  ConsentBanner — 3-category cookie consent (GDPR)                   */
/*  Categories: Nödvändiga (always on), Analys, Marknad               */
/*  Persists decision; dismissing hides PostHog                        */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

const CONSENT_STORAGE_KEY = 'hjalptorget_consent'

interface ConsentChoices {
  necessary: true // always on
  analytics: boolean
  marketing: boolean
}

function loadConsent(): ConsentChoices | null {
  try {
    const raw = window.localStorage?.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ConsentChoices
  } catch {
    return null
  }
}

function saveConsent(choices: ConsentChoices) {
  try {
    window.localStorage?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choices))
  } catch {
    // localStorage unavailable — consent still applied for session
  }
}

export function ConsentBanner() {
  const { t } = useTranslation()
  const setConsent = useUIStore((s) => s.setConsent)
  const [visible, setVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    const stored = loadConsent()
    if (stored) {
      setConsent(stored.analytics || stored.marketing)
    } else {
      setVisible(true)
    }
  }, [setConsent])

  const handleAcceptAll = useCallback(() => {
    const choices: ConsentChoices = { necessary: true, analytics: true, marketing: true }
    saveConsent(choices)
    setConsent(true)
    setVisible(false)
  }, [setConsent])

  const handleAcceptSelected = useCallback(() => {
    const choices: ConsentChoices = { necessary: true, analytics, marketing }
    saveConsent(choices)
    setConsent(analytics || marketing)
    setVisible(false)
  }, [analytics, marketing, setConsent])

  const handleDeclineAll = useCallback(() => {
    const choices: ConsentChoices = { necessary: true, analytics: false, marketing: false }
    saveConsent(choices)
    setConsent(false)
    setVisible(false)
  }, [setConsent])

  if (!visible) return null

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 border-t border-neutral-200 bg-neutral-0 p-4 shadow-lg',
        'md:bottom-4 md:left-4 md:right-auto md:max-w-md md:rounded-xl md:border',
        // Above MobileNav on mobile
        'pb-[calc(1rem+env(safe-area-inset-bottom,0px))] md:pb-4',
      )}
      role="dialog"
      aria-label={t('consent.title')}
    >
      <div className="space-y-3">
        <p className="text-sm font-semibold text-neutral-900">{t('consent.title')}</p>
        <p className="text-sm text-neutral-700">{t('consent.description')}</p>

        {showDetails && (
          <div className="space-y-3 rounded-lg bg-neutral-50 p-3">
            {/* Necessary — always on */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-900">{t('consent.necessary')}</p>
                <p className="text-xs text-neutral-500">{t('consent.necessaryDesc')}</p>
              </div>
              <Switch checked disabled aria-label={t('consent.necessary')} />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-900">{t('consent.analytics')}</p>
                <p className="text-xs text-neutral-500">{t('consent.analyticsDesc')}</p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                aria-label={t('consent.analytics')}
              />
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-900">{t('consent.marketing')}</p>
                <p className="text-xs text-neutral-500">{t('consent.marketingDesc')}</p>
              </div>
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                aria-label={t('consent.marketing')}
              />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" onClick={handleAcceptAll}>
            {t('consent.acceptAll')}
          </Button>
          {showDetails ? (
            <Button variant="secondary" size="sm" onClick={handleAcceptSelected}>
              {t('consent.saveChoices')}
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setShowDetails(true)}>
              {t('consent.customize')}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleDeclineAll}>
            {t('consent.declineAll')}
          </Button>
        </div>
      </div>
    </div>
  )
}

ConsentBanner.displayName = 'ConsentBanner'
